
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Queue } from "@/types/queue";
import { useToast } from "@/hooks/use-toast";

interface CreateQueueDialogProps {
  onCreateQueue: (queue: Omit<Queue, "id">) => Promise<void>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateQueueDialog({ onCreateQueue, open, onOpenChange }: CreateQueueDialogProps) {
  const [name, setName] = useState("");
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onCreateQueue({
        name,
        current_number: 0,
        people_in_queue: 0,
        avg_wait_time: 0,
        description: "",
        last_updated: new Date().toISOString(),
        created_at: new Date().toISOString(),
        user_id: null,
        is_active: true,
        can_be_deleted: true
      });
      setName("");
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to create queue. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Queue</DialogTitle>
          <DialogDescription>
            Fill in the details below to create a new queue for your business.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Queue Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter queue name"
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Queue"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
